import { useState, useEffect, useRef } from "react";
import { Vendor } from "@po-management/types";
import { useApiClient } from "../../auth/useApiClient";

interface VendorSelectorProps {
  value: Vendor | null;
  onChange: (vendor: Vendor | null) => void;
  onCreateNew: () => void;
  disabled?: boolean;
}

export function VendorSelector({ value, onChange, onCreateNew, disabled }: VendorSelectorProps): JSX.Element {
  const [query, setQuery] = useState("");
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filtered, setFiltered] = useState<Vendor[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApiClient();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    get("/api/vendors")
      .then((r) => r.json())
      .then((body: { success: boolean; data: Vendor[] }) => {
        if (body.success) setVendors(body.data);
        else setError("Failed to load vendors");
      })
      .catch(() => setError("Failed to load vendors"))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If the selected vendor isn't in the list (e.g. just created), add it
  useEffect(() => {
    if (value && !vendors.some((v) => v.id === value.id)) {
      setVendors((prev) => [...prev, value].sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [value, vendors]);

  // Filter on query change
  useEffect(() => {
    const q = query.toLowerCase();
    setFiltered(
      q.length === 0
        ? vendors
        : vendors.filter((v) => v.name.toLowerCase().includes(q))
    );
  }, [query, vendors]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const displayValue = value ? value.name : "";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        placeholder={loading ? "Loading vendors..." : "Search or select a vendor..."}
        value={open ? query : displayValue}
        disabled={disabled || loading}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        style={{ width: "100%", padding: "8px", boxSizing: "border-box", border: "1px solid #ccc", borderRadius: 4 }}
        aria-label="Vendor selector"
      />
      {error && <div style={{ color: "red", fontSize: 12 }}>{error}</div>}
      {open && !disabled && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #ccc", borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 100, maxHeight: 240, overflowY: "auto",
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: "8px 12px", color: "#666", fontSize: 14 }}>
              No vendors found
            </div>
          )}
          {filtered.map((v) => (
            <div
              key={v.id}
              onMouseDown={() => { onChange(v); setOpen(false); setQuery(""); }}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: 14,
                background: value?.id === v.id ? "#f0f4ff" : "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.background = value?.id === v.id ? "#f0f4ff" : "transparent")}
            >
              <div style={{ fontWeight: 500 }}>{v.name}</div>
              <div style={{ fontSize: 12, color: "#888" }}>{v.vat} · {v.taxOffice}</div>
            </div>
          ))}
          <div
            onMouseDown={onCreateNew}
            style={{
              padding: "8px 12px", cursor: "pointer", fontSize: 14,
              color: "#5B6A8A", fontWeight: 500, borderTop: "1px solid #eee",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            + Create new vendor
          </div>
        </div>
      )}
    </div>
  );
}
